import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import OrcaXBgLogo from '../../../assets/img/OrcaXBgLogo.jpg';
import {
  Button,
  Card,
  Form,
  Container,
  Col,
  Alert
} from "react-bootstrap";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardClasses, setCardClasses] = useState("card-hidden");

  // Fade-in effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCardClasses("");
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Basic check for parameter presence
  useEffect(() => {
    if (!token || !email) {
      setErrorMsg("Invalid recovery link. Please request a new password reset link.");
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!token || !email) {
      setErrorMsg("Cannot reset password without a valid link token and email address.");
      return;
    }

    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        email,
        token,
        password
      });

      if (response.data.success) {
        setSuccessMsg(response.data.message || "Password updated successfully. Redirecting to login...");
        setTimeout(() => {
          navigate("/auth/login");
        }, 3000);
      } else {
        setErrorMsg(response.data.message || "Failed to update password.");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setErrorMsg(err.response?.data?.message || "An error occurred while updating your password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="section-image h-100"
      style={{
        backgroundImage: `url(${OrcaXBgLogo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "contain"
      }}
    >
      <div style={{ backgroundColor: "transparent" }} className="content d-flex align-items-center p-0 my-auto h-100">
        <Container>
          <Col className="mx-auto" lg="6" md="8">
            <Form className="form border-0" onSubmit={handleSubmit}>
              <Card className={"card-login rounded " + cardClasses} style={{ border: "none", boxShadow: "none", background: "transparent" }}>
                {/* Header */}
                <Card.Header style={{ background: "transparent" }} className="text-center pt-4 pb-2">
                  <h3 style={{ fontWeight: "700", fontSize: "2.3rem" }} className="text-center mb-1">Set New Password</h3>
                  <small className="text-muted fs-6 font-weight-bold">
                    Enter and confirm your secure new password
                  </small>
                </Card.Header>

                {/* Body */}
                <Card.Body>
                  {errorMsg && <Alert variant="danger" className="text-center">{errorMsg}</Alert>}
                  {successMsg && <Alert variant="success" className="text-center">{successMsg}</Alert>}

                  {/* Password Field */}
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "700", fontSize: ".9rem" }} className="text-dark text-capitalize">
                      New Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={!token || !email}
                      className="fs-6 py-2"
                      style={{ borderRadius: "8px", border: "2px solid #dee2e6" }}
                    />
                  </Form.Group>

                  {/* Confirm Password Field */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: "700", fontSize: ".9rem" }} className="text-dark text-capitalize">
                      Confirm New Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={!token || !email}
                      className="fs-6 py-2"
                      style={{ borderRadius: "8px", border: "2px solid #dee2e6" }}
                    />
                  </Form.Group>
                </Card.Body>

                {/* Footer */}
                <Card.Footer className="text-center">
                  <Button
                    type="submit"
                    className="w-100 btn-wd fw-bolder py-2 px-4 fs-6 mb-3"
                    disabled={isSubmitting || !token || !email}
                    style={{ borderRadius: "8px", backgroundColor: "#014c95", border: "none" }}
                  >
                    {isSubmitting ? "Updating Password..." : "Update Password"}
                  </Button>

                  <p className="mt-4 mb-0">
                    <small style={{ fontWeight: "700", fontSize: ".9rem" }}>
                      Back to{" "}
                      <Link to="/auth/login" className="fw-bold" style={{ color: "#0090ff" }}>
                        Sign In
                      </Link>
                    </small>
                  </p>
                </Card.Footer>
              </Card>
            </Form>
          </Col>
        </Container>
      </div>
    </div>
  );
}

export default ResetPassword;
